import React, { useEffect, useState } from "react";
import { Button, TextField, Modal, Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DatePickerWithTimeComponent from "../DatePickerWithTimeComponent";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import { showCustomToast } from "../Misc/CustomToast";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
  deleteObject,
} from "firebase/storage";
import UploaderDocForNote from "../Updloader/UploaderDocForNote";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import MovieCreationIcon from "@mui/icons-material/MovieCreation";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteIcon from "@mui/icons-material/Delete";
import "./EditAftercareNoteRedesign.css";

function EditAftercareNote(props) {
  const { currentUser } = useAuth();
  const [note, setNote] = useState(props.note || props.noteContent || "");
  const [effectiveDateTime, setEffectiveDateTime] = useState(
    props.effectiveDateTime?.toDate
      ? props.effectiveDateTime.toDate()
      : props.effectiveDate || new Date()
  );
  const [existingFiles, setExistingFiles] = useState(props.fileUrls || []);
  const [newFiles, setNewFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (props.note || props.noteContent) {
      setNote(props.note || props.noteContent || "");
    }

    if (props.effectiveDateTime || props.effectiveDate) {
      const d = props.effectiveDateTime || props.effectiveDate;
      const dateObj = d?.toDate ? d.toDate() : d;
      if (dateObj) setEffectiveDateTime(new Date(dateObj));
    }

    if (props.fileUrls?.length) setExistingFiles(props.fileUrls);
  }, [
    props.note,
    props.noteContent,
    props.effectiveDateTime,
    props.effectiveDate,
    props.fileUrls,
  ]);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const filtered = selectedFiles.filter((f) => f.size <= 50 * 1024 * 1024);
    const rejected = selectedFiles.filter((f) => f.size > 50 * 1024 * 1024);

    if (rejected.length) {
      showCustomToast("Some files exceed 50MB and were skipped.", "error");
    }

    if (filtered.length) setHasChanged(true);
    setNewFiles((prev) => [...prev, ...filtered]);
  };

  const uploadFilesAndGetUrls = async (docId) => {
    if (!newFiles.length) return [];

    const storage = getStorage();
    const fileUrls = [];

    for (let i = 0; i < newFiles.length; i++) {
      const file = newFiles[i];
      const prefix = props.clientId
        ? `clients/${props.clientId}`
        : "aftercareNotes";
      const filePath = `Documents/${prefix}/${docId}/${file.name}`;
      const storageRef = ref(storage, filePath);
      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);
      fileUrls.push({ url: fileUrl, name: file.name });
    }

    return fileUrls;
  };

  const getStorageRefFromUrl = (url = "") => {
    try {
      const storage = getStorage();
      const path = decodeURIComponent(url.split("/o/")[1].split("?")[0]);
      return ref(storage, path);
    } catch {
      return null;
    }
  };

  const handleUpdate = async () => {
    if (!note || !effectiveDateTime) {
      showCustomToast("Please fill all mandatory fields.", "error");
      return;
    }

    setIsLoading(true);

    try {
      const docRef = doc(db, "notes", props.noteId);
      let mergedFiles = existingFiles || [];

      if (newFiles.length) {
        const uploaded = await uploadFilesAndGetUrls(props.noteId);
        mergedFiles = [...mergedFiles, ...uploaded];
      }

      await updateDoc(docRef, {
        noteContent: note,
        effectiveDateTime,
        updateAt: serverTimestamp(),
        doneBy: currentUser?.displayName || currentUser?.email || "Unknown",
        ...(mergedFiles?.length ? { fileUrls: mergedFiles } : {}),
      });

      setExistingFiles(mergedFiles);
      setNewFiles([]);
      setIsLoading(false);
      showCustomToast("Aftercare note updated successfully.", "success");
      props.closemodal();
    } catch (e) {
      console.error("Error updating aftercare note:", e);
      showCustomToast("Error when updating note", "error");
      setIsLoading(false);
    }
  };

  const getFileIcon = (name = "") => {
    const ext = name.split(".").pop()?.toLowerCase();

    if (["pdf"].includes(ext)) {
      return <PictureAsPdfIcon sx={{ color: "#d32f2f" }} />;
    }

    if (["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(ext)) {
      return <ImageIcon sx={{ color: "#1976d2" }} />;
    }

    if (["mp3", "wav", "aac", "ogg", "m4a"].includes(ext)) {
      return <AudiotrackIcon sx={{ color: "#8e24aa" }} />;
    }

    if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext)) {
      return <MovieCreationIcon sx={{ color: "#6d4c41" }} />;
    }

    if (["zip", "rar", "7z"].includes(ext)) {
      return <FolderZipIcon sx={{ color: "#f57c00" }} />;
    }

    if (["doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv"].includes(ext)) {
      return <DescriptionIcon sx={{ color: "#2e7d32" }} />;
    }

    return <InsertDriveFileIcon sx={{ color: "#607d8b" }} />;
  };

  const handleDeleteAttachment = async () => {
    if (!fileToDelete) return;

    const updated = existingFiles.filter((f) => f.url !== fileToDelete.url);

    try {
      const docRef = doc(db, "notes", props.noteId);
      await updateDoc(docRef, {
        fileUrls: updated,
        updateAt: serverTimestamp(),
      });

      const refFrom = getStorageRefFromUrl(fileToDelete.url);
      if (refFrom) {
        try {
          await deleteObject(refFrom);
        } catch (e) {
          console.warn("Failed to delete file from storage:", e);
        }
      }

      setExistingFiles(updated);
      setHasChanged(true);
    } catch (e) {
      console.error("Failed to delete attachment:", e);
      showCustomToast("Error removing attachment", "error");
    } finally {
      setShowDeleteConfirm(false);
      setFileToDelete(null);
    }
  };

  return (
    <div className="edit-aftercare-note-modal">
      <div className="edit-aftercare-note-header">
        <div>
          <h2>Edit aftercare note</h2>
          <p>Update the note content, effective date, and attachments.</p>
        </div>
        <button
          type="button"
          className="edit-aftercare-note-close"
          onClick={props.closemodal}
          aria-label="Close edit aftercare note modal"
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>

      <div className="edit-aftercare-note-body">
        <section className="edit-aftercare-note-section">
          <div className="edit-aftercare-note-section-title">
            <span>Note details</span>
          </div>

          <div className="edit-aftercare-note-textarea">
            <div className="edit-aftercare-note-label">
              Note content <span>*</span>
            </div>
            <TextField
              name="aftercareNote"
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                setHasChanged(true);
              }}
              placeholder="Type aftercare note here"
              multiline
              rows={10}
              fullWidth
            />
          </div>

          <div className="edit-aftercare-note-date-row">
            <DatePickerWithTimeComponent
              title="Effective date and time"
              value={effectiveDateTime}
              onChange={(d) => {
                setEffectiveDateTime(d);
                setHasChanged(true);
              }}
            />
          </div>
        </section>

        <section className="edit-aftercare-note-section">
          <div className="edit-aftercare-note-section-title">
            <span>Attachments</span>
            <strong>{existingFiles.length + newFiles.length} files</strong>
          </div>

          {existingFiles?.length ? (
            <div className="edit-aftercare-note-file-grid">
              {existingFiles.map((f, idx) => (
                <div className="edit-aftercare-note-file" key={idx}>
                  <a href={f.url} target="_blank" rel="noreferrer">
                    {getFileIcon(f.name)}
                    <span>{f.name || "File"}</span>
                  </a>
                  <IconButton
                    size="small"
                    aria-label="Delete attachment"
                    onClick={() => {
                      setFileToDelete(f);
                      setShowDeleteConfirm(true);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
              ))}
            </div>
          ) : (
            <div className="edit-aftercare-note-empty-state">
              No existing attachments.
            </div>
          )}

          {newFiles?.length ? (
            <div className="edit-aftercare-note-file-grid">
              {newFiles.map((f, idx) => (
                <div className="edit-aftercare-note-file pending" key={idx}>
                  {getFileIcon(f.name)}
                  <span>{f.name}</span>
                </div>
              ))}
            </div>
          ) : null}

          <div className="edit-aftercare-note-uploader">
            <UploaderDocForNote
              handleFileChange={handleFileChange}
              files={newFiles}
            />
            <p>Max file size: 50 MB each.</p>
          </div>
        </section>
      </div>

      <div className="edit-aftercare-note-footer">
        <Button
          variant="outlined"
          className="edit-aftercare-note-cancel"
          onClick={props.closemodal}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          className="edit-aftercare-note-save"
          onClick={handleUpdate}
          disabled={isLoading || !hasChanged}
        >
          {isLoading ? "Updating..." : "Update note"}
        </Button>
      </div>

      <Modal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <Box className="edit-aftercare-note-delete-box">
          <div className="edit-aftercare-note-delete-title">
            Remove this attachment?
          </div>
          <div className="edit-aftercare-note-delete-file">
            {fileToDelete?.name}
          </div>
          <div className="edit-aftercare-note-delete-actions">
            <Button
              variant="outlined"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleDeleteAttachment}>
              Delete
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
}

export default EditAftercareNote;
